import { useState } from "react";
import { ChevronDown, Clock, GraduationCap, Layers, Users } from "lucide-react";

const DIFFICULTY_CHIP_STYLES = {
  easy: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  medium: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  hard: "border-rose-500/30 bg-rose-500/10 text-rose-400",
};

function getDifficultyChipClass(difficulty) {
  const n = (difficulty || "").trim().toLowerCase();
  if (n === "easy" || n === "low") return DIFFICULTY_CHIP_STYLES.easy;
  if (n === "medium" || n === "intermediate")
    return DIFFICULTY_CHIP_STYLES.medium;
  if (n === "hard" || n === "advanced" || n === "high")
    return DIFFICULTY_CHIP_STYLES.hard;
  return "border-(--color-border) text-(--color-text-muted)";
}

// time_estimate arrives as a string of minutes, e.g. "150".
function formatEstimate(minutesValue) {
  const minutes = Number.parseInt(minutesValue, 10);
  if (!minutes) return null;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function MetaChip({ icon: Icon, children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium ${className}`}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </span>
  );
}

// Mirrors the briefing header so the detail page doesn't jump when data lands.
export function ScenarioBriefingSkeleton() {
  return (
    <section className="rounded-xl border border-(--color-border) qv-card qv-noise p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="qv-skeleton h-7 w-64 rounded" />
        <div className="flex gap-2">
          <div className="qv-skeleton h-6 w-20 rounded-md" />
          <div className="qv-skeleton h-6 w-16 rounded-md" />
          <div className="qv-skeleton h-6 w-20 rounded-md" />
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        <div className="qv-skeleton h-3 w-full rounded" />
        <div className="qv-skeleton h-3 w-11/12 rounded" />
      </div>
    </section>
  );
}

export default function ScenarioBriefing({ scenario, totalStages, teamCount }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const estimate = formatEstimate(scenario?.time_estimate);
  const neutralChip = "border-(--color-border) text-(--color-text-secondary)";

  return (
    <section className="rounded-xl border border-(--color-border) qv-card qv-noise p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-xl font-semibold text-(--color-text-primary)">
          {scenario?.title || "Scenario"}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          {scenario?.difficulty && (
            <MetaChip className={getDifficultyChipClass(scenario.difficulty)}>
              {scenario.difficulty}
            </MetaChip>
          )}
          {estimate && (
            <MetaChip icon={Clock} className={neutralChip}>
              {estimate}
            </MetaChip>
          )}
          {totalStages > 0 && (
            <MetaChip icon={Layers} className={neutralChip}>
              {totalStages} stages
            </MetaChip>
          )}
          <MetaChip icon={Users} className={neutralChip}>
            {teamCount} {teamCount === 1 ? "team" : "teams"}
          </MetaChip>
        </div>
      </div>

      {scenario?.description && (
        <p
          className={`mt-3 text-xs leading-relaxed text-(--color-text-muted) ${isExpanded ? "" : "line-clamp-2"}`}
        >
          {scenario.description}
        </p>
      )}

      {isExpanded && scenario?.learning_objectives && (
        <div className="mt-4 rounded-lg border border-(--color-border) bg-gray-50/80 dark:bg-black/20 p-3">
          <p className="mb-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-(--color-text-muted)">
            <GraduationCap className="h-3.5 w-3.5" />
            Learning objectives
          </p>
          <p className="text-xs leading-relaxed text-(--color-text-secondary)">
            {scenario.learning_objectives}
          </p>
        </div>
      )}

      {(scenario?.description || scenario?.learning_objectives) && (
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-(--color-primary) hover:underline"
        >
          {isExpanded ? "Show less" : "Read more"}
          <ChevronDown
            className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </section>
  );
}
